<template>
  <div class="flex flex-col md:flex-row gap-4 md:gap-16 w-full py-3 pr-0 md:pr-24">
    <div
      v-if="canReadPaymentRules"
      class="flex flex-col flex-1 rounded-lg overflow-hidden bg-[#F0F0F4]"
    >
      <template v-if="originalRepassMode">
        <div class="flex items-center gap-2 px-4 h-full" :class="repassOriginalContainerClasses">
          <SVGIcon :path="repassOriginalIcon" class="w-4 h-4" :class="repassOriginalIconClasses" />
          <div class="flex flex-col text-left">
            <span class="font-bold text-md" :class="repassOriginalTextClasses"
              >Modalidade de repasse</span
            >
            <span class="text-sm" :class="repassOriginalTextClasses">{{
              formatRepassMode(originalRepassMode)
            }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2 px-4 h-full" :class="repassRequestedContainerClasses">
          <SVGIcon
            v-if="repassModeStatus !== 'Rejected'"
            :path="mdiLockOpenVariant"
            class="w-4 h-4"
            :class="repassRequestedIconClasses"
          />
          <div v-else class="w-4 h-4 mt-1"></div>

          <div class="flex flex-col text-left">
            <span class="font-bold text-md" :class="repassRequestedTextClasses"
              >Modalidade de repasse</span
            >
            <span class="text-sm" :class="repassRequestedValueTextClasses">{{
              formatRepassMode(currentRepassMode)
            }}</span>
          </div>
        </div>
      </template>

      <template v-else>
        <div v-if="canReadPaymentRules" class="flex flex-col gap-1 text-left p-4">
          <span class="font-bold text-sm text-[#212529]">Modalidade de repasse</span>
          <span class="text-sm text-[#212529]">{{ formatRepassMode(currentRepassMode) }}</span>
        </div>
      </template>
    </div>

    <div class="flex flex-col flex-1 rounded-lg overflow-hidden bg-[#F0F0F4]">
      <template v-if="originalProportionalityRule">
        <div class="flex items-center gap-2 px-4 py-2" :class="propRuleOriginalContainerClasses">
          <SVGIcon
            :path="propRuleOriginalIcon"
            class="w-4 h-4"
            :class="propRuleOriginalIconClasses"
          />

          <div class="flex flex-col text-left">
            <span class="font-bold text-md" :class="propRuleOriginalTextClasses"
              >Regra de proporcionalidade</span
            >
            <span class="text-sm" :class="propRuleOriginalTextClasses">
              {{ getProportionalityRuleTitle(originalProportionalityRule) }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-2 px-4 py-2" :class="propRuleRequestedContainerClasses">
          <SVGIcon
            v-if="proportionalityRuleStatus !== 'Rejected'"
            :path="mdiLockOpenVariant"
            class="w-4 h-4"
            :class="propRuleRequestedIconClasses"
          />
          <div v-else class="w-4 h-4 mt-1"></div>

          <div v-if="canReadPaymentRules" class="flex flex-col text-left">
            <span class="font-bold text-md" :class="propRuleRequestedTextClasses"
              >Regra de proporcionalidade</span
            >
            <span class="text-sm" :class="propRuleRequestedValueTextClasses">
              {{ getProportionalityRuleTitle(currentProportionalityRule) }}
            </span>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="flex flex-col gap-1 text-left p-4">
          <span class="font-bold text-sm text-[#212529]">Regra de proporcionalidade</span>
          <span class="text-sm text-[#212529]">
            {{ getProportionalityRuleTitle(currentProportionalityRule) }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { mdiMinusCircleOutline, mdiLockOpenVariant, mdiLock } from '@mdi/js'
import { getProportionalityRuleTitle } from '@/utils/schedule'
import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

const BG_NEUTRAL = 'bg-[#F0F0F4]'
const BG_HIGHLIGHT = 'bg-[#FCE5E8]'
const TEXT_MUTED = 'text-gray-400'
const TEXT_DEFAULT = 'text-[#212529]'
const TEXT_PRIMARY30 = 'text-primary30'
const TEXT_NEGATIVE = 'text-negative'

type ApprovalStatus = 'Approved' | 'Rejected' | null

const props = defineProps<{
  originalRepassMode: 'FLOW' | 'IMMEDIATE' | null | undefined
  currentRepassMode: 'FLOW' | 'IMMEDIATE' | null | undefined
  originalProportionalityRule: ProportionalityRuleEnum | null | undefined
  currentProportionalityRule: ProportionalityRuleEnum | null | undefined
  repassModeStatus?: ApprovalStatus
  proportionalityRuleStatus?: ApprovalStatus
}>()

const permissionsStore = usePermissionsStore()

const canReadPaymentRules = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationPaymentRulesReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const formatRepassMode = (mode?: 'FLOW' | 'IMMEDIATE' | null): string => {
  if (mode === 'FLOW') return 'Fluxo'
  if (mode === 'IMMEDIATE') return 'À vista'
  return 'Não definida'
}

const repassOriginalContainerClasses = computed(() => {
  if (props.repassModeStatus === 'Approved') return BG_NEUTRAL
  if (props.repassModeStatus === 'Rejected')
    return 'rounded-lg border-2 border-primary30 bg-[#E2E2E9]'
  return BG_NEUTRAL
})

const repassOriginalTextClasses = computed(() => {
  return props.repassModeStatus === 'Approved' ? TEXT_MUTED : TEXT_DEFAULT
})

const repassOriginalIcon = computed(() => {
  return props.repassModeStatus === 'Rejected' ? mdiLock : mdiMinusCircleOutline
})

const repassOriginalIconClasses = computed(() => {
  if (props.repassModeStatus === 'Approved') return TEXT_MUTED
  if (props.repassModeStatus === 'Rejected') return TEXT_PRIMARY30
  return TEXT_NEGATIVE
})

const repassRequestedContainerClasses = computed(() => {
  if (props.repassModeStatus === 'Approved') return BG_HIGHLIGHT
  if (props.repassModeStatus === 'Rejected') return BG_NEUTRAL
  return BG_HIGHLIGHT
})

const repassRequestedTextClasses = computed(() => {
  return props.repassModeStatus === 'Rejected' ? TEXT_MUTED : TEXT_DEFAULT
})

const repassRequestedValueTextClasses = computed(() => {
  if (props.repassModeStatus === 'Rejected') return TEXT_MUTED
  return TEXT_DEFAULT
})

const repassRequestedIconClasses = computed(() => {
  if (props.repassModeStatus === 'Rejected') return TEXT_MUTED
  return TEXT_NEGATIVE
})

const propRuleOriginalContainerClasses = computed(() => {
  if (props.proportionalityRuleStatus === 'Approved')
    return 'rounded-lg border-2 border-[#F0F0F4] bg-[#F0F0F4]'
  if (props.proportionalityRuleStatus === 'Rejected')
    return 'rounded-lg border-2 border-primary30 bg-[#E2E2E9]'
  return BG_NEUTRAL
})

const propRuleOriginalTextClasses = computed(() => {
  return props.proportionalityRuleStatus === 'Approved' ? TEXT_MUTED : TEXT_DEFAULT
})

const propRuleOriginalIcon = computed(() => {
  return props.proportionalityRuleStatus === 'Rejected' ? mdiLock : mdiMinusCircleOutline
})

const propRuleOriginalIconClasses = computed(() => {
  if (props.proportionalityRuleStatus === 'Approved') return TEXT_MUTED
  if (props.proportionalityRuleStatus === 'Rejected') return TEXT_PRIMARY30
  return TEXT_NEGATIVE
})

const propRuleRequestedContainerClasses = computed(() => {
  if (props.proportionalityRuleStatus === 'Approved') return BG_HIGHLIGHT
  if (props.proportionalityRuleStatus === 'Rejected') return BG_NEUTRAL
  return BG_HIGHLIGHT
})

const propRuleRequestedTextClasses = computed(() => {
  return props.proportionalityRuleStatus === 'Rejected' ? TEXT_MUTED : TEXT_DEFAULT
})

const propRuleRequestedValueTextClasses = computed(() => {
  if (props.proportionalityRuleStatus === 'Rejected') return TEXT_MUTED
  return TEXT_DEFAULT
})

const propRuleRequestedIconClasses = computed(() => {
  if (props.proportionalityRuleStatus === 'Rejected') return TEXT_MUTED
  return TEXT_NEGATIVE
})
</script>
